/**
 * Utility functions for order handling
 */

import { Order } from './types';

/**
 * Format order ID for human-readable display
 * 
 * Takes the last 4 characters of the order ID (excluding hyphens) for a 
 * customer-friendly reference. The full UUID is kept internally for all 
 * database and API operations.
 * 
 * Examples:
 * - "550e8400-e29b-41d4-a716-446655440000" → "0000"
 * - "abc123" → "C123"
 * 
 * @param orderId - The full order ID (typically a UUID)
 * @returns The last 4 characters in uppercase, or "N/A" if invalid
 */
export const formatOrderIdForDisplay = (orderId: string): string => {
    if (!orderId) return 'N/A';
    // Remove hyphens from UUID and get last 4 characters
    const cleanId = orderId.replace(/-/g, '');
    return cleanId.slice(-4).toUpperCase();
};

/**
 * Get the display address for an order
 * 
 * For backward compatibility:
 * - If legacy 'address' field has content, use it
 * - Otherwise, combine the new structured fields: {number}, {street}, {area}, {postcode}
 * 
 * @param order - Order object (or partial with address fields)
 * @param fallback - Fallback text if no address available (default: 'Not provided')
 * @returns The formatted address string for display
 */
export const getDisplayAddress = (
    order: Pick<Order, 'address' | 'number' | 'street' | 'area' | 'postcode'> | null | undefined,
    fallback: string = 'Not provided'
): string => {
    if (!order) return fallback;
    
    // Check structured fields FIRST (new format takes priority)
    const parts: string[] = [];
    
    if (order.number?.trim()) {
        parts.push(order.number.trim());
    }
    
    if (order.street?.trim()) {
        parts.push(order.street.trim());
    }
    
    if (order.area?.trim()) {
        parts.push(order.area.trim());
    }
    
    if (order.postcode?.trim()) {
        // Format postcode with space if it's 7 chars without space
        const pc = order.postcode.trim().toUpperCase();
        if (pc.length === 7 && !pc.includes(' ')) {
            parts.push(pc.slice(0, 4) + ' ' + pc.slice(4));
        } else {
            parts.push(pc);
        }
    }
    
    // If we have structured address parts, use them
    if (parts.length > 0) {
        return parts.join(', ');
    }
    
    // Fall back to legacy address field
    if (order.address && order.address.trim()) {
        return order.address.trim();
    }
    
    return fallback;
};
